import { useEffect } from 'react';
import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';

export default function OpenLetterPage() {
  useAnalytics('Open Letter to Governor Newsom');

  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Open Letter to Governor Newsom: The Time for AI Engineering is Now | Algorithmic Consistency Initiative';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionContent = 'An urgent call to California Governor Gavin Newsom and the Legislature: Move beyond debate to implementation. The Constitutional AI Playbook provides the engineering framework for trustworthy, auditable AI systems that protect civil rights while enabling innovation.';
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descriptionContent;
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Governor Newsom, California AI regulation, SB 1047, Constitutional AI, NIST AI RMF, AI safety engineering, AI governance, civil rights AI, trustworthy AI, auditable AI');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Governor Newsom, California AI regulation, SB 1047, Constitutional AI, NIST AI RMF, AI safety engineering, AI governance, civil rights AI, trustworthy AI, auditable AI';
      document.head.appendChild(meta);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://algorithmicconsistency.org/open-letter-governor-newsom');

    // Open Graph Tags
    const ogTags = [
      { property: 'og:title', content: 'Open Letter to Governor Newsom: The Time for AI Engineering is Now' },
      { property: 'og:description', content: descriptionContent },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://algorithmicconsistency.org/open-letter-governor-newsom' },
      { property: 'og:site_name', content: 'Algorithmic Consistency Initiative' },
      { property: 'article:published_time', content: '2025-01-15' },
      { property: 'article:author', content: 'Alberto Rocha, Angel Luevano, Argentina Davalos-Luevano' }
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });

    // Twitter Card Tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Open Letter to Governor Newsom: The Time for AI Engineering is Now' },
      { name: 'twitter:description', content: descriptionContent }
    ];

    twitterTags.forEach(tag => {
      let twitterTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', tag.name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute('content', tag.content);
    });

    // Structured Data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Open Letter to Governor Newsom: The Time for AI Engineering is Now",
      "author": [
        {
          "@type": "Person",
          "name": "Alberto Rocha"
        },
        {
          "@type": "Person",
          "name": "Angel Luevano"
        },
        {
          "@type": "Person",
          "name": "Argentina Davalos-Luevano"
        }
      ],
      "publisher": {
        "@type": "Organization",
        "name": "Algorithmic Consistency Initiative",
        "url": "https://algorithmicconsistency.org"
      },
      "datePublished": "2025-01-15",
      "description": descriptionContent,
      "articleBody": "An open letter calling for engineering-first AI regulation in California",
      "keywords": "AI regulation, Constitutional AI, Governor Newsom, California, NIST AI RMF"
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

    // Last Modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString());

  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-red-50 to-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold mb-6">
              URGENT CALL TO ACTION
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
              Open Letter to Governor Newsom and California Legislature
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              A civil rights message from Angel Luevano calling for immediate action on Constitutional AI implementation to protect Californians from algorithmic harm.
            </p>
            <a 
              href="#letter-content"
              className="inline-block px-8 py-4 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-semibold text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
            >
              Read Letter
            </a>
          </div>
        </section>

        {/* Letter Content */}
        <article id="letter-content" className="container mx-auto px-6 lg:px-12 py-16 max-w-4xl">
          
          {/* Opening */}
          <div className="mb-12 pb-8 border-b border-gray-200">
            <p className="text-xl text-gray-800 leading-relaxed mb-6">
              <strong className="text-gray-900">Governor Newsom,</strong>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              In your veto of SB 1047, you rightfully demanded that California's approach to AI regulation be based on "empirical trajectory analysis" rather than fear. You asked for a framework that prioritizes science and evidence over broad, precautionary bans.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong className="text-gray-900">We agree.</strong> The era of debating the possibilities of AI safety is over. It is time to implement the realities.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              At The Algorithmic Consistency Initiative (ACI), we reject the false binary between "stifling innovation" and "accepting risk." We propose the <strong className="text-red-700">"Third Way"</strong>: a rigorous, engineering-first approach that transforms AI safety from a philosophical art into a hard science.
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              We Are Not Guessing. We Have the Playbook.
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              While the industry struggles with the "Black Box" problem of unpredictability, ACI has developed the operational <strong>Playbook for Constitutional AI</strong>. This is not a theoretical policy paper; it is an implementation guide for converting abstract civil rights laws into executable code.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our framework provides the specific mechanisms required to operationalize the NIST AI Risk Management Framework:
            </p>

            {/* Three Pillars */}
            <div className="space-y-6 my-8">
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  1. GOVERN via Constitution
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We replace vague "human feedback" (which leads to sycophancy and bias) with explicit <strong>Normative Constraints</strong>. We draft the "Constitution"—the prioritized set of rules—before a single line of code is trained.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  2. MEASURE via Error Rates
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We do not rely on "vibes." We calculate the <strong>Constitutional Error Rate (CER)</strong>, measuring exactly how often a model fails to adhere to its governance clauses.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  3. MANAGE via Auditable Traces
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We abolish the "Black Box." Our architecture forces the AI to produce a <strong>Reasoning Trace</strong>—a step-by-step, human-readable log explaining why it made a decision, citing the specific Constitutional clause it followed.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              From "Foreseeable Defects" to "Engineered Trust"
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Current industry standards rely on <strong>Behavioral Mimicry</strong>—training models to mimic human raters. Legally, this creates a "Digital Mirror" that reflects our worst biases and constitutes a foreseeable design defect.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              ACI offers the remedy: <strong className="text-red-700">Constitutional AI</strong>. By moving from "trying to be safe" to "proving compliance," we unlock a future where California's AI sector is not only the most powerful in the world but the most legally defensible and trustworthy.
            </p>
          </section>

          {/* The Offer */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              The Offer
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We are done talking about what should happen. We are ready to show you how it happens.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              ACI invites your administration to review our <strong>Constitutional AI Playbook</strong>. We stand ready to partner with the state to pilot these standards, proving that you can have innovation and safety—if you have the courage to engineer it.
            </p>
          </section>

          {/* Signature */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-lg text-gray-800 mb-2">
              <strong>Sincerely,</strong>
            </p>
            <p className="text-lg text-gray-800 mb-1">
              <strong>Alberto Rocha, Angel Luevano, & Argentina Davalos-Luevano</strong>
            </p>
            <p className="text-gray-700 mb-1">
              Co-Founders, The Algorithmic Consistency Initiative (ACI)
            </p>
            <p className="text-gray-600 italic mb-4">
              Engineering Civil Rights into the Digital Age
            </p>
            <a 
              href="https://algorithmicconsistency.org" 
              className="text-red-700 hover:text-red-800 font-medium cursor-pointer"
              target="_blank"
              rel="noopener noreferrer"
            >
              AlgorithmicConsistency.org
            </a>
          </div>

        </article>

        {/* The Evidence Section */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                The Evidence
              </h2>
              <p className="text-xl text-gray-600">
                Our framework is backed by comprehensive research and technical documentation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Infographic */}
              <div className="bg-white rounded-lg p-8 shadow-lg">
                <div className="mb-6">
                  <img 
                    src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/c9d6ef372c25d61bf94134911131db49.webp"
                    alt="From Black Box to Glass Box: The Constitutional AI Revolution"
                    className="w-full h-auto rounded-lg object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  From Black Box to Glass Box
                </h3>
                <p className="text-gray-700 mb-4">
                  Visual guide to the Constitutional AI framework and how it transforms opaque AI systems into auditable, trustworthy technology.
                </p>
                <a 
                  href="/infographics"
                  className="inline-block px-6 py-3 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  View Full Infographic
                </a>
              </div>

              {/* Book Playbook - Placeholder for book cover */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="relative mb-4">
                  <div className="absolute top-4 left-4 bg-red-800 text-white px-3 py-1 text-xs font-bold z-10 whitespace-nowrap">
                    IN DEVELOPMENT
                  </div>
                  <img 
                    src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/d8a653f0c5e1b828aa552af92565980d.png"
                    alt="The Constitutional AI Implementation Playbook"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">The Constitutional AI Implementation Playbook</h4>
                <p className="text-sm text-slate-600 mb-4">A comprehensive guide for operationalizing Constitutional AI in practice</p>
                <div className="flex gap-3">
                  <a 
                    href="/book-proposal" 
                    className="flex-1 text-center bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Full Proposal
                  </a>
                  <a 
                    href="/playbook-executive-summary" 
                    className="flex-1 text-center bg-red-800 hover:bg-red-900 text-white px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Executive Summary
                  </a>
                </div>
              </div>

            </div>

            {/* White Papers */}
            <div className="mt-12 bg-white rounded-lg p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Supporting Research & White Papers
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a 
                  href="/constitutional-ai"
                  className="p-6 border border-gray-200 rounded-lg hover:border-red-600 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <i className="ri-file-text-line text-2xl text-red-600 group-hover:text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Constitutional AI Framework
                  </h4>
                  <p className="text-sm text-gray-600">
                    Technical foundations and implementation guide
                  </p>
                </a>

                <a 
                  href="/ai-liability"
                  className="p-6 border border-gray-200 rounded-lg hover:border-red-600 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <i className="ri-scales-3-line text-2xl text-red-600 group-hover:text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    AI Liability Framework
                  </h4>
                  <p className="text-sm text-gray-600">
                    Legal accountability in autonomous systems
                  </p>
                </a>

                <a 
                  href="/cai-engineering-standard"
                  className="p-6 border border-gray-200 rounded-lg hover:border-red-600 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <i className="ri-settings-3-line text-2xl text-red-600 group-hover:text-white"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    CAI Engineering Standard
                  </h4>
                  <p className="text-sm text-gray-600">
                    Technical standards and best practices
                  </p>
                </a>
              </div>

              <div className="mt-8 text-center">
                <a 
                  href="/publications"
                  className="inline-block px-8 py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  View All Publications
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* Press Inquiry CTA */}
        <section className="py-20 px-6 bg-gray-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Press Inquiries & Partnership Opportunities
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Media representatives and government officials: Contact us to discuss this initiative and explore partnership opportunities.
            </p>
            <a 
              href="/#contact"
              className="inline-block px-8 py-4 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
            >
              Contact ACI Leadership
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
